import moment from "moment";
import { Property, property_classes, property_structures, property_types } from "@/models/properties.model";
import { FormCreator, FormCreatorProps, FormFieldDef } from "@/components/shared/form-creator";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useState } from "react";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet.tsx";
import { capitalize } from "@/lib/utils";

const formSchema = z.object({
  image: z.union([z.instanceof(File), z.string()]).optional(),
  name: z.string().min(1, "Name is required"),
  description: z.string().optional(),
  year_built: z.coerce.number().min(1800).max(moment().year()),
  floors: z.coerce.number().min(1),
  type: z.array(z.nativeEnum(property_types)).nonempty("At least one type is required"),
  structure: z.array(z.nativeEnum(property_structures)).nonempty("At least one structure is required"),
  class: z.array(z.nativeEnum(property_classes)).nonempty("At least one class is required"),
  address: z.string().min(1),
  website: z.string().url("Must be a valid URL").optional(),
});

type FormValues = z.infer<typeof formSchema>;

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (values: Partial<Property>) => Promise<void>;
}

export function PropertyModal({ open, onSubmit, onOpenChange }: Props) {
  const [loading, setLoading] = useState(false);

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      image: undefined,
      name: "",
      description: "",
      year_built: moment().year(),
      floors: 1,
      type: [],
      structure: [],
      class: [],
      address: "",
    },
  });

  const fields: FormFieldDef[] = [
    { type: "file", name: "image", label: "Property Image" },
    { type: "text", name: "name", label: "Property Name", required: true },
    { type: "textarea", name: "description", label: "Description" },
    { type: "number", name: "year_built", label: "Year Built", required: true },
    { type: "number", name: "floors", label: "Floors", required: true },
    {
      type: "select",
      name: "type",
      label: "Type",
      required: true,
      multiple: true,
      options: Object.values(property_types).map((t) => ({ label: capitalize(t), value: t })),
    },
    {
      type: "select",
      name: "structure",
      label: "Structure",
      required: true,
      multiple: true,
      options: Object.values(property_structures).map((s) => ({ label: capitalize(s), value: s })),
    },
    {
      type: "select",
      name: "class",
      label: "Class",
      required: true,
      multiple: true,
      options: Object.values(property_classes).map((c) => ({ label: c, value: c })),
    },
    {
      type: "address",
      name: "address",
      label: "Address",
      required: true,
      placeholder: "Start typing address of property",
    },
    { type: "text", name: "website", label: "Website (URL)" },
  ];

  const props: FormCreatorProps = {
    form,
    onSubmit: async (values: FormValues) => {
      setLoading(true);
      try {
        await onSubmit(values);
        onOpenChange(false);
        form.reset();
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    },
    formDefinition: fields,
    buttonText: loading ? "Creating..." : "Create Property",
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent>
        <SheetHeader>
          <SheetTitle>Add New Property</SheetTitle>
          <SheetDescription>
            <FormCreator {...props} />
          </SheetDescription>
        </SheetHeader>
      </SheetContent>
    </Sheet>
  );
}
